import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type { AccountingContactCreate, AccountingContactUpdate } from '@/lib/schemas';

// ============================================================================
// Query Keys
// ============================================================================

function accountingContactsKey(orgId: string | undefined, estId?: number) {
  return ['accounting-contacts', orgId, estId ?? null] as const;
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetches accounting contacts for an org, or an establishment within an org.
 */
export function useAccountingContacts(
  organizationId: string | undefined,
  establishmentId?: number,
  options?: Omit<UseQueryOptions<any, Error, any, any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: accountingContactsKey(organizationId, establishmentId),
    queryFn: () => api.accountingContacts.list(organizationId!, establishmentId),
    enabled: !!organizationId,
    ...options,
  });
}

/**
 * Projects of this client that have their OWN saved contacts (R1 AC6). Only meaningful for the client-level
 * list; the client page shows a line per project under the default contacts.
 */
export function useProjectRecipientSets(organizationId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['project-recipient-sets', organizationId] as const,
    queryFn: () => api.accountingContacts.projectRecipientSets(organizationId!),
    enabled: !!organizationId && enabled,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Creates a new accounting contact for an org or establishment.
 *
 * v0.14 (2026-05-29): Pass the create payload through whole instead of
 * explicit-picking {name, title, emails}. Explicit-pick silently drops new
 * fields added to AccountingContactCreate (cross-cutting hunt S2 +
 * feedback_mutation_hook_field_drift.md). Routing-only fields
 * (organizationId, establishmentId) are stripped before send.
 */
export function useCreateAccountingContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      establishmentId,
      ...data
    }: {
      organizationId: string;
      establishmentId?: number;
    } & AccountingContactCreate) =>
      api.accountingContacts.create(organizationId, data, establishmentId),
    onSuccess: async (_response, variables) => {
      // v0.14.5 R2: await — same stale-list race class as org contacts. The
      // recipients picker reads accounting contacts; a non-awaited invalidate
      // could show a pre-insert pool until reload.
      await queryClient.invalidateQueries({
        queryKey: accountingContactsKey(variables.organizationId, variables.establishmentId),
      });
      // R1 AC17: the client page's other list reads the same organization. A person added here must
      // never leave a stale copy there, and the invoice picker must offer them straight away.
      await queryClient.invalidateQueries({ queryKey: ['organization-contacts', variables.organizationId] });
      toast.success('Accounting contact added');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add accounting contact');
    },
  });
}

/**
 * Updates an existing accounting contact.
 * Update/delete paths are org-scoped only (no establishment in the URL).
 */
export function useUpdateAccountingContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      organizationId: string;
      contactId: string;
      establishmentId?: number;  // For cache invalidation only
    } & AccountingContactUpdate) =>
      api.accountingContacts.update(data.organizationId, data.contactId, {
        name: data.name,
        title: data.title,
        emails: data.emails,
      }),
    onSuccess: async (_response, variables) => {
      // v0.14.5 R2: await. Prefix key (['accounting-contacts', orgId]) so both
      // org-level and establishment-scoped caches refetch before settle.
      await queryClient.invalidateQueries({
        queryKey: ['accounting-contacts', variables.organizationId],
      });
      await queryClient.invalidateQueries({ queryKey: ['organization-contacts', variables.organizationId] });
      toast.success('Accounting contact updated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update accounting contact');
    },
  });
}

/**
 * Fetches resolved accounting contacts for an invoice (establishment → org fallback).
 * Used by InvoiceDetailPage and SendInvoiceButton to pre-fill send dialog.
 */
export function useResolvedAccountingContacts(
  invoiceId: string | undefined,
  options?: Omit<UseQueryOptions<any, Error, any, any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['resolved-accounting-contacts', invoiceId] as const,
    queryFn: () => api.invoices.getResolvedAccountingContacts(invoiceId!),
    enabled: !!invoiceId,
    staleTime: 60_000,
    ...options,
  });
}

/**
 * Deletes an accounting contact.
 */
export function useDeleteAccountingContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      organizationId: string;
      contactId: string;
      establishmentId?: number;  // For cache invalidation only
    }) =>
      api.accountingContacts.delete(data.organizationId, data.contactId),
    onSuccess: async (_response, variables) => {
      // v0.14.5 R2: await (same race class).
      await queryClient.invalidateQueries({
        queryKey: ['accounting-contacts', variables.organizationId],
      });
      await queryClient.invalidateQueries({ queryKey: ['organization-contacts', variables.organizationId] });
      await queryClient.invalidateQueries({ queryKey: ['project-recipient-sets', variables.organizationId] });
      toast.success('Accounting contact removed');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to remove accounting contact');
    },
  });
}

/**
 * Change a default contact's TO/CC role and/or move them up or down (R1 AC6). The server answers with the whole
 * group in saved order; the cached lists for this client are refetched so every surface shows the same order.
 */
export function useUpdateAccountingContactRoleOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      organizationId: string;
      contactId: string;
      accounting_role?: 'to' | 'cc';
      move?: 'up' | 'down';
    }) =>
      api.accountingContacts.updateRoleOrder(data.organizationId, data.contactId, {
        ...(data.accounting_role ? { accounting_role: data.accounting_role } : {}),
        ...(data.move ? { move: data.move } : {}),
      }),
    onSuccess: async (_response, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['accounting-contacts', variables.organizationId] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Could not change this contact');
    },
  });
}
